import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../components/Alert.jsx';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';

// First-time admin setup. Active only while the users table is empty;
// /api/admin/bootstrap returns 403 once a user exists, so we guard here too.
export default function Setup() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [available, setAvailable] = useState(null); // null while checking
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [startggToken, setStartggToken] = useState('');
  const [overlayKey, setOverlayKey] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    api.bootstrapStatus()
      .then((data) => { if (alive) setAvailable(!!data?.available); })
      .catch(() => { if (alive) setAvailable(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (available === false) navigate('/login', { replace: true });
  }, [available, navigate]);

  function generateOverlayKey() {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    setOverlayKey(`nv-${hex}`);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.bootstrap({
        username: username.trim(),
        password,
        startggToken: startggToken.trim(),
        overlayKey: overlayKey.trim(),
      });
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Setup failed.');
    } finally {
      setSubmitting(false);
    }
  }

  if (available == null) {
    return (
      <div className="container py-5 text-center text-muted">Checking setup…</div>
    );
  }

  return (
    <div className="row justify-content-center mt-5">
      <div className="col-md-8 col-lg-6">
        <div className="text-center mb-4">
          <h1>
            <span className="text-neon-magenta">STREAM</span>
            <span className="text-neon-blue">GG</span>
          </h1>
          <p className="text-muted">First-Time Setup</p>
        </div>

        {error && <Alert kind="danger" onClose={() => setError(null)}>{error}</Alert>}

        <div className="card">
          <div className="card-header text-center">
            <h4 className="mb-0">Create the admin account</h4>
          </div>
          <div className="card-body p-4">
            <p className="text-muted small mb-3">
              This page is only available once. Pick credentials, paste your start.gg
              API token and a random overlay key, and you'll be logged in.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="username" className="form-label">Admin username</label>
                <input
                  type="text"
                  className="form-control"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="mb-3">
                <label htmlFor="password" className="form-label">
                  Password <span className="text-muted small">(8+ characters)</span>
                </label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </div>

              <div className="mb-3">
                <label htmlFor="startggToken" className="form-label">
                  start.gg API token
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="startggToken"
                  value={startggToken}
                  onChange={(e) => setStartggToken(e.target.value)}
                  required
                />
                <span className="form-text">
                  From <a href="https://start.gg/admin/profile/developer" target="_blank" rel="noreferrer">start.gg developer settings</a>.
                </span>
              </div>

              <div className="mb-3">
                <label htmlFor="overlayKey" className="form-label">
                  Overlay key <span className="text-muted small">(secret string in OBS URLs)</span>
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="overlayKey"
                    value={overlayKey}
                    onChange={(e) => setOverlayKey(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={generateOverlayKey}
                  >
                    Generate
                  </button>
                </div>
                <span className="form-text">
                  Anyone with this key can read your overlay endpoints. Keep it private.
                </span>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg w-100"
                disabled={submitting}
              >
                {submitting ? 'Creating…' : 'Create admin & sign in'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
